import { type RowData, functionalUpdate } from '@tanstack/react-table';
import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import { WrapTextIcon, WrapTextOffIcon } from '@dynatrace/strato-icons';

import type { DataTableLineWrapProps } from './line-wrap-types.js';
import { Button } from '../../../../buttons/button/Button.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import {
  type Column,
  type Table,
  type TableFeature,
  type TableOptions,
} from '../../hooks/useTable/types.js';
import { dataTableMessages } from '../../messages.js';

/**
 * Global line wrap action for the DataTable to be used in the DataTable.Toolbar
 *
 * @public
 */
export function DataTableGlobalLineWrapAction() {
  return null;
}

/**
 * Feature implementation for LineWrap. This contains the logic that will be given
 * to the tanstack table. The implementing interface is important as these are
 * the function that will be called by the tanstack table core during render.
 * @internal
 */
export const LineWrap: TableFeature = {
  createColumn<TData extends RowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    column.toggleLineWrap = (value) => {
      if (!column.getCanLineWrap()) {
        return;
      }

      table.setColumnLineWrap((oldValue) => {
        const columnsStatesToUpdate = computeLineWrapStateUpdate(column, value);

        // Check if the lineWrap was set as a global first
        if (typeof oldValue === 'boolean') {
          const columnIds = table
            .getAllLeafColumnsWithoutBuiltin()
            .map((col) => col.id);

          // when switching from a boolean, we need to preserve the current state of the other columns
          const currentColumnsState: Record<string, boolean> =
            Object.fromEntries(columnIds.map((id) => [id, oldValue]));

          // When toggling a parent column, we need to set the state of all child columns
          return {
            ...currentColumnsState,
            ...columnsStatesToUpdate,
          };
        }

        return {
          ...oldValue,
          ...columnsStatesToUpdate,
        };
      });
    };

    column.getIsLineWrapped = () => {
      const lineWrapState = table.getState().columnLineWrap;
      // Boolean values define a global line wrapping on the table.
      if (typeof lineWrapState === 'boolean') {
        return lineWrapState;
      }

      const childColumns = column.columns;
      if (childColumns.length > 0) {
        // If the current column is a parent, we consider it line wrapped only if all child columns are line wrapped
        return childColumns.every((child) => child.getIsLineWrapped());
      }
      return lineWrapState?.[column.id] ?? false;
    };

    column.getCanLineWrap = () => {
      return table.options.enableLineWrap ?? true;
    };
  },

  createTable<TData extends RowData>(table: Table<TData>): void {
    table.setColumnLineWrap = (updater) => {
      const newValue = functionalUpdate(
        updater,
        table.getState().columnLineWrap ?? false,
      );
      table.options.onColumnLineWrapChange?.(newValue);
    };
  },
};

/**
 * Determines which columns need to have their lineWrap state updated.
 * @param column - The column for which lineWrap was toggled.
 * @param value - The new lineWrap value to set. If not provided, the current value will be toggled.
 * @returns a record of column IDs and their new lineWrap states.
 */
function computeLineWrapStateUpdate<TData extends RowData>(
  column: Column<TData>,
  value?: boolean,
): Record<string, boolean> {
  const newValue = value ?? !column.getIsLineWrapped();
  const childColumns = column.columns;

  if (childColumns.length === 0) {
    // For leaf or standalone columns, we only need to update the toggled column
    return {
      [column.id]: newValue,
    };
  }
  // If the toggled column is a parent column, we need to toggle all its child columns
  return Object.fromEntries(childColumns.map((child) => [child.id, newValue]));
}

/**
 * Configuration hook for the DataTable LineWrap feature.
 * @internal
 */
export function useColumnLineWrap<T>(
  props: DataTableLineWrapProps,
  options: TableOptions<T>,
): void {
  const configLineWrap = useDataTableConfigElement('lineWrap');

  const [columnLineWrap, onColumnLineWrapChange] = useControllableState({
    value: props.lineWrap ?? configLineWrap,
    defaultValue: props.defaultLineWrap,
    onChange: props.onLineWrapChange,
  });

  options.state = { ...options.state, columnLineWrap };
  options.onColumnLineWrapChange = onColumnLineWrapChange;
}

//# region Providing actions

/**
 * Provides the toolbar button that allows toggling global line wrap.
 * @internal
 */
export function DataTableGlobalLineWrapToolbarAction(): JSX.Element {
  const table = useDataTable();
  const intl = useIntl();

  const lineWrapState = table.getState().columnLineWrap;
  const Icon = lineWrapState === true ? <WrapTextOffIcon /> : <WrapTextIcon />;
  const lineWrapLabel =
    lineWrapState === true
      ? intl.formatMessage(dataTableMessages.disableLineWrapLabel)
      : intl.formatMessage(dataTableMessages.enableLineWrapLabel);

  const lineWrapDtProperty = `state:${
    lineWrapState === true ? 'unwrap' : 'wrap'
  }`;

  return (
    <Tooltip text={lineWrapLabel}>
      <Button
        aria-label={lineWrapLabel}
        data-dt-name="ToolbarLineWrap"
        data-dt-properties={lineWrapDtProperty}
        onClick={() =>
          table.setColumnLineWrap((oldLineWrap) => {
            if (oldLineWrap === true) {
              return {};
            }
            return true;
          })
        }
      >
        <Button.Prefix>{Icon}</Button.Prefix>
      </Button>
    </Tooltip>
  );
}

//# endregion Providing actions
